'use client';
import { MultiServicePreset } from '@/types';

import Button from '@/components/Button';
import SectionTitle from '@/components/SectionTitle';
import Subheader from '@/components/Subheader';
import SimpleText from '@/components/SimpleText';
import { useConfig } from '@/app/contexts/ConfigContext';
import CustomLink from '@/components/CustomLink';
import { SERVICE_TYPES } from '@/helpers/enums';
import { isStringLongerThanMaxLength } from '@/helpers';
import { URL_S3 } from '@/helpers/constants';

export default function TextOneSection() {
  const { wording, config, setConfig, updateWording, selectedLanguage } =
    useConfig();
  const textOneServiceType = config.multiService === MultiServicePreset.AML
    ? SERVICE_TYPES.MEDITATION
    : config.serviceType;
  const handleTextOneConfig = () => {
    if (config.multiService !== MultiServicePreset.AML) {
      return;
    }

    setConfig({
      ...config,
      serviceType: SERVICE_TYPES.MEDITATION,
    });
    updateWording(SERVICE_TYPES.MEDITATION, selectedLanguage);
  };

  const titleLanguageSpecificStyle = () => {
    if (
      isStringLongerThanMaxLength(
        wording?.components_text1_card_3_text as string,
        173,
      )
    ) {
      return {
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
        marginBottom: '0px',
      };
    }
  };

  const textLanguageSpecificStyle = () => {
    if (
      isStringLongerThanMaxLength(
        wording?.components_text1_card_3_text as string,
        173,
      )
    ) {
      return {
        fontSize: '0.75rem',
        lineHeight: '1rem',
      };
    }
  };

  return (
    <section className='xl:grid xl:grid-cols-2 xl:gap-x-16 xl:mb-32 mb-8'>
      <div className='flex flex-col'>
        <SectionTitle>{wording?.components_text1_section_title}</SectionTitle>
        <Subheader className='mt-4 sm:mb-8 mb-3'>
          {wording?.components_text1_subheader_text}
        </Subheader>
        <span
          className='text-neutral-500 sm:text-xl text-sm font-medium sm:leading-[29px] leading-tight'
          style={{ color: config.theme.textColorDarkSecondary }}
        >
          {wording?.components_text1_span_text}
        </span>
        <div className='xl:mt-16 mt-5 mb-4 grid grid-cols-1 gap-3 xl:block z-10'>
          <div
            className='sm:p-16 py-4 px-3 bg-zinc-100'
            style={{ backgroundColor: config.theme.backgroundColorSecondary }}
          >
            <h3
              className='mb-3 text-neutral-800 sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
              style={{ color: config.theme.textColorDark }}
            >
              {wording?.components_text1_card_1_title}
            </h3>
            <SimpleText>{wording?.components_text1_card_1_text}</SimpleText>
            <div className='sm:mt-8 mt-3'>
              <img
                className='hidden sm:block relative'
                src={`${URL_S3}/${textOneServiceType}/images/text1-home-1.webp`}
                alt='image'
              />
              <img
                className='block sm:hidden relative'
                src={`${URL_S3}/${textOneServiceType}/images/text1-home-1-mobile.webp`}
                alt='image'
              />
            </div>
          </div>
          <div
            className='xl:hidden py-4 px-3 bg-primary'
            style={{ backgroundColor: config.theme.accentColorOne }}
          >
            <div>
              <h3
                className='mb-3 text-neutral-800 sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
                style={{ color: config.theme.textColorDark }}
              >
                {wording?.components_text1_card_2_title}
              </h3>
              <SimpleText className='text-neutral-800'>
                {wording?.components_text1_card_2_text}
              </SimpleText>
            </div>
          </div>
        </div>
      </div>
      {/* Use in desktop to change the position of the "cards" */}
      <div className='xl:flex flex-col hidden'>
        <div className='relative'>
          <img
            src={`${URL_S3}/${textOneServiceType}/images/text1-home-2.webp`}
            alt='image'
          />
          <div className='absolute bottom-0 z-10 p-16'>
            <h3
              className='mb-3 text-white sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
              style={{ color: config.theme.textColorLight }}
            >
              {wording?.components_text1_card_3_title}
            </h3>
            <SimpleText
              className='text-white'
              style={{ color: config.theme.textColorLight }}
            >
              {wording?.components_text1_card_3_text}
            </SimpleText>
          </div>
        </div>
        <div
          className='mt-12 p-16 bg-primary'
          style={{ backgroundColor: config.theme.accentColorOne }}
        >
          <div>
            <h3
              className='mb-3 text-neutral-800 sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
              style={{ color: config.theme.textColorDark }}
            >
              {wording?.components_text1_card_2_title}
            </h3>
            <SimpleText className='text-neutral-800'>
              {wording?.components_text1_card_2_text}
            </SimpleText>
          </div>
        </div>
      </div>
      <div className='xl:hidden sm:p-8 p-2 relative sm:min-h-[300px] min-h-[148px] overflow-hidden'>
        <div className='relative z-10 sm:min-h-[236px] min-h-[148px]'>
          <h3
            className='mb-3 text-white sm:text-3xl font-bold sm:leading-[33px] text-xl leading-snug'
            style={{
              color: config.theme.textColorLight,
              ...titleLanguageSpecificStyle(),
            }}
          >
            {wording?.components_text1_card_3_title}
          </h3>
          <SimpleText
            className='text-white'
            style={{
              color: config.theme.textColorLight,
              ...textLanguageSpecificStyle(),
            }}
          >
            {wording?.components_text1_card_3_text}
          </SimpleText>
        </div>
        <img
          className='absolute inset-0 w-full h-full object-cover'
          src={`${URL_S3}/${textOneServiceType}/images/text1-home-2.webp`}
          alt='image'
        />
      </div>
      <div className='mt-8 xl:col-span-2 text-center'>
        <Button themeBackgroundColor={config.theme.accentColorOne}>
          <CustomLink
            href='/text1'
            style={{ color: config.theme.textColorDark }}
            onClick={handleTextOneConfig}
          >
            {wording?.components_text1_link}
          </CustomLink>
        </Button>
      </div>
    </section>
  );
}
